import * as z from 'zod';

export const ConfirmOrDeclineBookingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to confirm or decline.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paymentStatus: z
        .enum([
          'UNDEFINED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_PAID',
          'REFUNDED',
          'EXEMPT',
        ])
        .optional(),
    })
    .optional(),
});
export const ConfirmOrDeclineBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Updated booking.')
    .optional(),
});
export const BulkConfirmOrDeclineBookingRequest = z.object({
  details: z
    .array(
      z.object({
        bookingId: z
          .string()
          .describe('ID of the booking to confirm or decline.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .optional(),
      })
    )
    .min(1)
    .max(300),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the confirmed or declined booking objects.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkConfirmOrDeclineBookingResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the booking that was created or updated.\nIncluding its ID, index in the bulk request and whether it was\nsuccessfully created or updated.'
          )
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      slot: z.never().optional(),
                      schedule: z.never().optional(),
                    }),
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .describe(
                                    'Information about how the customer has selected the resource for this resource type.'
                                  )
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                )
                .optional(),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .describe(
                  "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                )
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .describe(
                  "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                )
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .describe(
                  "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                )
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe(
            'Created or updated booking. Available only if you requested\nto return the booking entity.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total successes and failures of the Bulk Confirm Or Decline call.'
    )
    .optional(),
});
export const CancelBookingAnonymouslyRequest = z.object({
  token: z.string().describe('Anonymous token for the booking.').max(2048),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be passed when canceling the booking.'
    ),
});
export const CancelBookingAnonymouslyResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Canceled booking.')
    .optional(),
});
export const RescheduleBookingAnonymouslyRequest = z.object({
  token: z.string().describe('Anonymous token for the booking.').max(2048),
  slot: z
    .object({
      sessionId: z
        .string()
        .describe(
          'Identifier for the underlying session when the session is a single session or generated from a recurring session.'
        )
        .optional()
        .nullable(),
      serviceId: z
        .string()
        .describe('Service identifier. Required.')
        .optional(),
      scheduleId: z
        .string()
        .describe('Schedule identifier. Required.')
        .optional(),
      startDate: z
        .string()
        .describe(
          'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
        )
        .optional()
        .nullable(),
      endDate: z
        .string()
        .describe(
          'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
        )
        .optional()
        .nullable(),
      timezone: z
        .string()
        .describe(
          'The timezone according to which the slot is calculated and presented.'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe('Resource ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Resource name.')
            .max(1200)
            .optional()
            .nullable(),
          transparency: z.enum(['OPAQUE', 'TRANSPARENT']).optional(),
        })
        .describe(
          'The resource required for this slot.\nWhen populated, the specified resource will be assigned to the slot upon confirmation according to its availability.\nWhen empty, if `skip_availability_validation` is `false`, a random available resource will be assigned to the slot upon confirmation.\nOtherwise, one of the service resources will be assigned to the slot randomly upon confirmation.'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              'Business Location ID. Present if the location is a business location.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z.string().describe('Location name.').optional().nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          locationType: z
            .enum(['UNDEFINED', 'OWNER_BUSINESS', 'OWNER_CUSTOM', 'CUSTOM'])
            .optional(),
        })
        .describe('Geographic location of the slot.')
        .optional(),
      eventId: z
        .string()
        .describe(
          'Calendar event ID - not supported.\nIf not empty, on all write flows (create/update), it takes priority over `sessionId`.\nSo if both `sessionId` and `eventId` are provided, the `sessionId` will be based on the `eventId`.\nOtherwise, if `eventId` is empty on write flow,'
        )
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      resourceSelections: z
        .array(
          z.object({
            resourceTypeId: z
              .string()
              .describe(
                'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            selectionMethod: z
              .enum(['SPECIFIC_RESOURCE', 'ANY_RESOURCE', 'NO_SELECTION'])
              .optional(),
          })
        )
        .max(3)
        .optional(),
    })
    .describe('New slot to reschedule the booking to.'),
  options: z.object({
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be passed when rescheduling the booking.'
      ),
  }),
});
export const RescheduleBookingAnonymouslyResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Rescheduled booking.')
    .optional(),
});
export const GetBookingAnonymouslyRequest = z.object({
  token: z.string().describe('Anonymous token for the booking.').max(2048),
  options: z
    .object({
      fields: z
        .array(z.enum(['BOOKING_FEES']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const GetBookingAnonymouslyResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Retrieved booking.')
    .optional(),
  allowedAnonymousActions: z
    .object({
      cancel: z
        .boolean()
        .describe('Whether the booking can be canceled anonymously.')
        .optional(),
      reschedule: z
        .boolean()
        .describe('Whether the booking can be rescheduled anonymously.')
        .optional(),
    })
    .describe('Allowed anonymous actions for this booking.')
    .optional(),
  bookingFee: z
    .object({
      _id: z
        .string()
        .describe('Booking fee ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookingId: z
        .string()
        .describe('ID of the booking associated with the fee.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      cancellationFee: z
        .object({
          price: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe('Price the customer must pay.')
            .optional(),
          status: z
            .enum([
              'UNKNOWN_STATUS',
              'PREVIEW',
              'NOT_YET_APPLIED_TO_ORDER',
              'APPLIED_TO_ORDER',
            ])
            .describe('Status of the booking fee.')
            .optional(),
          trigger: z
            .enum(['UNKNOWN_TRIGGER', 'NOT_ATTENDED', 'BOOKING_CANCELED'])
            .describe(
              'Information about what triggered the creation of the booking fee.'
            )
            .optional(),
        })
        .describe('Cancellation fee details.')
        .optional(),
      policyDetails: z
        .object({
          _id: z
            .string()
            .describe('ID of the booking policy.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          description: z
            .string()
            .describe('Description of the booking policy.')
            .max(2500)
            .optional()
            .nullable(),
          descriptionTranslated: z
            .string()
            .describe(
              'Translated description of the booking policy according to the buyer language of the eCommerce order.'
            )
            .max(2500)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the [booking policy snapshot](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policy-snapshots/introduction)\naccording to which the fee was created.'
        )
        .optional(),
    })
    .describe(
      'Booking fee details.\n\nReturned only when `BOOKING_FEES` is passed in `fields`.'
    )
    .optional(),
});
export const GetServiceAnonymouslyRequest = z.object({
  token: z.string().describe('Anonymous token for the booking.').max(2048),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'STAFF_MEMBER_DETAILS',
            'RESOURCE_TYPE_DETAILS',
            'DISCOUNT_INFO_DETAILS',
          ])
        )
        .max(5)
        .optional(),
    })
    .optional(),
});
export const GetServiceAnonymouslyResponse = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Retrieved service.')
    .optional(),
});
export const GetAnonymousActionTokenRequest = z.object({
  bookingId: z
    .string()
    .describe('Booking ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAnonymousActionTokenResponse = z.object({
  token: z
    .string()
    .describe(
      'Encrypted, URL-safe token for anonymous access to the booking.\nPass this token to the other methods in this API to retrieve, cancel, or reschedule the booking without authentication.'
    )
    .min(32)
    .max(2048)
    .optional()
    .nullable(),
});
export const CreateMultiServiceBookingRequest = z.object({
  bookings: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Booking ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          bookedEntity: z
            .intersection(
              z.object({
                title: z
                  .string()
                  .describe(
                    'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                  )
                  .max(6000)
                  .optional()
                  .nullable(),
                tags: z.array(z.string()).optional(),
              }),
              z.xor([
                z.object({
                  schedule: z.never().optional(),
                  slot: z
                    .object({
                      sessionId: z
                        .string()
                        .describe(
                          'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                        )
                        .optional()
                        .nullable(),
                      serviceId: z.string().describe('Service ID.').optional(),
                      scheduleId: z
                        .string()
                        .describe('Schedule ID.')
                        .optional(),
                      eventId: z
                        .string()
                        .describe(
                          'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                        )
                        .min(36)
                        .max(250)
                        .optional()
                        .nullable(),
                      startDate: z
                        .string()
                        .describe(
                          'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                        )
                        .optional()
                        .nullable(),
                      endDate: z
                        .string()
                        .describe(
                          'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                        )
                        .optional()
                        .nullable(),
                      timezone: z
                        .string()
                        .describe(
                          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                        )
                        .optional()
                        .nullable(),
                      resource: z
                        .object({
                          _id: z
                            .string()
                            .describe("ID of the booking's primary resource.")
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe("Resource's name at the time of booking.")
                            .max(40)
                            .optional()
                            .nullable(),
                          email: z
                            .string()
                            .describe(
                              "Resource's email at the time of booking."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          scheduleId: z
                            .string()
                            .describe(
                              "ID of the schedule belonging to the booking's primary resource."
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                        )
                        .optional(),
                      location: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Location name.')
                            .optional()
                            .nullable(),
                          formattedAddress: z
                            .string()
                            .describe('The full address of this location.')
                            .optional()
                            .nullable(),
                          formattedAddressTranslated: z
                            .string()
                            .describe(
                              'The full translated address of this location.'
                            )
                            .max(512)
                            .optional()
                            .nullable(),
                          locationType: z
                            .enum([
                              'UNDEFINED',
                              'OWNER_BUSINESS',
                              'OWNER_CUSTOM',
                              'CUSTOM',
                            ])
                            .optional(),
                        })
                        .describe('Location where the session takes place.')
                        .optional(),
                      resourceSelections: z
                        .array(
                          z.object({
                            resourceTypeId: z
                              .string()
                              .describe(
                                'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional()
                              .nullable(),
                            selectionMethod: z
                              .enum([
                                'SPECIFIC_RESOURCE',
                                'ANY_RESOURCE',
                                'NO_SELECTION',
                              ])
                              .optional(),
                          })
                        )
                        .max(3)
                        .optional(),
                    })
                    .describe(
                      '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                    ),
                }),
                z.object({
                  slot: z.never().optional(),
                  schedule: z
                    .object({
                      scheduleId: z
                        .string()
                        .describe(
                          '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                        )
                        .optional(),
                      serviceId: z
                        .string()
                        .describe('Booked service ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      location: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Location name.')
                            .optional()
                            .nullable(),
                          formattedAddress: z
                            .string()
                            .describe('The full address of this location.')
                            .optional()
                            .nullable(),
                          formattedAddressTranslated: z
                            .string()
                            .describe(
                              'The full translated address of this location.'
                            )
                            .max(512)
                            .optional()
                            .nullable(),
                          locationType: z
                            .enum([
                              'UNDEFINED',
                              'OWNER_BUSINESS',
                              'OWNER_CUSTOM',
                              'CUSTOM',
                            ])
                            .optional(),
                        })
                        .describe(
                          "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                        )
                        .optional(),
                      timezone: z
                        .string()
                        .describe(
                          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                        )
                        .optional()
                        .nullable(),
                      firstSessionStart: z
                        .string()
                        .describe(
                          'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                        )
                        .optional()
                        .nullable(),
                      lastSessionEnd: z
                        .string()
                        .describe(
                          'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                    ),
                }),
              ])
            )
            .describe(
              'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
            ),
          contactDetails: z
            .object({
              contactId: z
                .string()
                .describe('Contact ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              firstName: z
                .string()
                .describe(
                  "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                )
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe("Contact's last name.")
                .optional()
                .nullable(),
              email: z
                .string()
                .describe(
                  "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                )
                .email()
                .optional()
                .nullable(),
              phone: z
                .string()
                .describe("Contact's phone number.")
                .optional()
                .nullable(),
              fullAddress: z
                .intersection(
                  z.object({
                    country: z
                      .string()
                      .describe('Country code.')
                      .optional()
                      .nullable(),
                    subdivision: z
                      .string()
                      .describe(
                        'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                      )
                      .optional()
                      .nullable(),
                    city: z
                      .string()
                      .describe('City name.')
                      .optional()
                      .nullable(),
                    postalCode: z
                      .string()
                      .describe('Zip/postal code.')
                      .optional()
                      .nullable(),
                    addressLine2: z
                      .string()
                      .describe(
                        'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                      )
                      .optional()
                      .nullable(),
                    formattedAddress: z
                      .string()
                      .describe(
                        'A string containing the full address of this location.'
                      )
                      .optional()
                      .nullable(),
                    hint: z
                      .string()
                      .describe('Free text to help find the address.')
                      .optional()
                      .nullable(),
                    geocode: z
                      .object({
                        latitude: z
                          .number()
                          .describe('Address latitude.')
                          .optional()
                          .nullable(),
                        longitude: z
                          .number()
                          .describe('Address longitude.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Coordinates of the physical address.')
                      .optional(),
                    countryFullname: z
                      .string()
                      .describe('Country full name.')
                      .optional()
                      .nullable(),
                    subdivisions: z
                      .array(
                        z.object({
                          code: z
                            .string()
                            .describe(
                              'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Subdivision full name.')
                            .optional(),
                        })
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z.never().optional(),
                    }),
                    z.object({
                      addressLine: z.never().optional(),
                      streetAddress: z
                        .object({
                          number: z
                            .string()
                            .describe('Street number.')
                            .optional(),
                          name: z.string().describe('Street name.').optional(),
                          apt: z
                            .string()
                            .describe('Apartment number.')
                            .optional(),
                        })
                        .describe('Street name, number and apartment number.'),
                    }),
                    z.object({
                      streetAddress: z.never().optional(),
                      addressLine: z
                        .string()
                        .describe(
                          'Main address line, usually street and number, as free text.'
                        ),
                    }),
                  ])
                )
                .describe("Contact's full address.")
                .optional(),
              timeZone: z
                .string()
                .describe("Contact's time zone.")
                .optional()
                .nullable(),
              countryCode: z
                .string()
                .describe(
                  "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
            )
            .optional(),
          additionalFields: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('ID of the form field as defined in the form.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
                value: z
                  .string()
                  .describe('Value that was submitted for this field.')
                  .optional()
                  .nullable(),
                label: z
                  .string()
                  .describe("Form field's label at the time of submission.")
                  .optional()
                  .nullable(),
                valueType: z
                  .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                  .optional(),
              })
            )
            .optional(),
          status: z
            .enum([
              'CREATED',
              'CONFIRMED',
              'CANCELED',
              'PENDING',
              'DECLINED',
              'WAITING_LIST',
            ])
            .optional(),
          paymentStatus: z
            .enum([
              'UNDEFINED',
              'NOT_PAID',
              'PAID',
              'PARTIALLY_PAID',
              'REFUNDED',
              'EXEMPT',
            ])
            .optional(),
          selectedPaymentOption: z
            .enum([
              'UNDEFINED',
              'OFFLINE',
              'ONLINE',
              'MEMBERSHIP',
              'MEMBERSHIP_OFFLINE',
            ])
            .optional(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          externalUserId: z
            .string()
            .describe('External user ID that you can provide.')
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
            )
            .optional()
            .nullable(),
          createdBy: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
            )
            .optional(),
          startDate: z
            .date()
            .describe(
              'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          doubleBooked: z
            .boolean()
            .describe(
              'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
            )
            .optional()
            .nullable(),
          formSubmissionId: z
            .string()
            .describe(
              'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          formId: z
            .string()
            .describe(
              "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          bookedAddOns: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('The ID of the add-on.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                groupId: z
                  .string()
                  .describe('The ID of the add-on group.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                durationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                  )
                  .min(1)
                  .max(1440)
                  .optional()
                  .nullable(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                  )
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Add-on `name` at the time of booking.')
                  .max(100)
                  .optional()
                  .nullable(),
                nameTranslated: z
                  .string()
                  .describe(
                    'Add-on name translated to the language the customer used during booking.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
              })
            )
            .max(21)
            .optional(),
          appId: z
            .string()
            .describe(
              'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          createdByAppId: z
            .string()
            .describe(
              "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          depositSelected: z
            .boolean()
            .describe(
              'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            totalParticipants: z.never().optional(),
            participantsChoices: z.never().optional(),
          }),
          z.object({
            participantsChoices: z.never().optional(),
            totalParticipants: z
              .number()
              .int()
              .describe(
                "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
              )
              .min(1),
          }),
          z.object({
            totalParticipants: z.never().optional(),
            participantsChoices: z
              .object({
                serviceChoices: z
                  .array(
                    z.object({
                      numberOfParticipants: z
                        .number()
                        .int()
                        .describe(
                          'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              optionId: z
                                .string()
                                .describe(
                                  'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                custom: z.never().optional(),
                                duration: z.never().optional(),
                              }),
                              z.object({
                                duration: z.never().optional(),
                                custom: z
                                  .string()
                                  .describe(
                                    'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                  ),
                              }),
                              z.object({
                                custom: z.never().optional(),
                                duration: z.object({
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                    )
                                    .min(1)
                                    .max(44639)
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                    )
                                    .max(255)
                                    .optional()
                                    .nullable(),
                                }),
                              }),
                            ])
                          )
                        )
                        .max(5)
                        .optional(),
                    })
                  )
                  .min(1)
                  .max(20)
                  .optional(),
              })
              .describe(
                'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
              ),
          }),
        ])
      )
    )
    .min(2)
    .max(8),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the session starts.\nWix Bookings takes the phone number from `contactDetails.phone`.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      flowControlSettings: z
        .object({
          skipAvailabilityValidation: z
            .boolean()
            .describe(
              "Whether the availability is checked before creating the booking.\n\n- `false`: A booking is only created when the slot or schedule is available.\n- `true`: The booking is created regardless of availability conflicts. Make sure the call's [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) has the required permissions.\n\n*Use cases for `true`:**\n- Emergency or priority bookings that must be accommodated.\n- Administrative bookings that override normal availability rules.\n- Testing or demonstration purposes.\n\nDefault: `false`."
            )
            .optional(),
          skipBusinessConfirmation: z
            .boolean()
            .describe(
              "Whether `PENDING` bookings are automatically set to `CONFIRMED` for\nservices that normally require the owner's manual confirmation.\n\nYour app must have the `BOOKINGS.OVERRIDE_AVAILABILITY` permission\nwhen passing `true`.\nDefault: `false`."
            )
            .optional(),
          skipSelectedPaymentOptionValidation: z
            .boolean()
            .describe(
              "Whether customers can pay using a payment method that isn't supported\nfor the service, but that's supported for other services.\n\nYour app must have the `BOOKINGS.MANAGE_PAYMENTS` permission when passing\n`true`.\nDefault: `false`."
            )
            .optional(),
          skipAddOnValidation: z
            .boolean()
            .describe(
              "Whether Wix Bookings validates [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) when creating a booking.\nThe calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions to specify `true`.\nThis allows customers to choose an add-on that's not associated with the service or choose more than the maximum number of add-ons from a group.\n\nDefault: `false`."
            )
            .optional(),
        })
        .describe(
          'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to skip checking availability before updating the status.'
        )
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the created single-service bookings.')
        .optional(),
      multiServiceBookingType: z
        .enum(['SEQUENTIAL_BOOKINGS', 'SEPARATE_BOOKINGS', 'PARALLEL_BOOKINGS'])
        .optional(),
    })
    .optional(),
});
export const CreateMultiServiceBookingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe(
      'Created multi-service booking.\nContains the single-service bookings in the same order as specified in the request.'
    )
    .optional(),
});
export const RescheduleMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking to reschedule.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  rescheduleBookingsInfo: z.array(
    z.intersection(
      z.object({
        bookingId: z
          .string()
          .describe('ID of the booking to reschedule.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        slot: z
          .object({
            sessionId: z
              .string()
              .describe(
                'Identifier for the underlying session when the session is a single session or generated from a recurring session.'
              )
              .optional()
              .nullable(),
            serviceId: z
              .string()
              .describe('Service identifier. Required.')
              .optional(),
            scheduleId: z
              .string()
              .describe('Schedule identifier. Required.')
              .optional(),
            startDate: z
              .string()
              .describe(
                'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
              )
              .optional()
              .nullable(),
            endDate: z
              .string()
              .describe(
                'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
              )
              .optional()
              .nullable(),
            timezone: z
              .string()
              .describe(
                'The timezone according to which the slot is calculated and presented.'
              )
              .optional()
              .nullable(),
            resource: z
              .object({
                _id: z
                  .string()
                  .describe('Resource ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Resource name.')
                  .max(1200)
                  .optional()
                  .nullable(),
                transparency: z.enum(['OPAQUE', 'TRANSPARENT']).optional(),
              })
              .describe(
                'The resource required for this slot.\nWhen populated, the specified resource will be assigned to the slot upon confirmation according to its availability.\nWhen empty, if `skip_availability_validation` is `false`, a random available resource will be assigned to the slot upon confirmation.\nOtherwise, one of the service resources will be assigned to the slot randomly upon confirmation.'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Business Location ID. Present if the location is a business location.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Location name.')
                  .optional()
                  .nullable(),
                formattedAddress: z
                  .string()
                  .describe(
                    'A string containing the full address of this location.'
                  )
                  .optional()
                  .nullable(),
                locationType: z
                  .enum([
                    'UNDEFINED',
                    'OWNER_BUSINESS',
                    'OWNER_CUSTOM',
                    'CUSTOM',
                  ])
                  .optional(),
              })
              .describe('Geographic location of the slot.')
              .optional(),
            eventId: z
              .string()
              .describe(
                'Calendar event ID - not supported.\nIf not empty, on all write flows (create/update), it takes priority over `sessionId`.\nSo if both `sessionId` and `eventId` are provided, the `sessionId` will be based on the `eventId`.\nOtherwise, if `eventId` is empty on write flow,'
              )
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            resourceSelections: z
              .array(
                z.object({
                  resourceTypeId: z
                    .string()
                    .describe(
                      'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  selectionMethod: z
                    .enum(['SPECIFIC_RESOURCE', 'ANY_RESOURCE', 'NO_SELECTION'])
                    .optional(),
                })
              )
              .max(3)
              .optional(),
          })
          .describe('Information about the new slot.'),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
          ),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              'Total number of participants. Available only for services with [variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\nSpecify when all participants book the same variant.'
            ),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the service choices to book. Available only for services with [variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\nSpecify when not all participants book the same variant.'
            ),
        }),
      ])
    )
  ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      flowControlSettings: z
        .object({
          ignoreReschedulePolicy: z
            .boolean()
            .describe(
              'Whether the rescheduling policy applies when rescheduling the booking.\n\nWhen passing `false`, you can only reschedule a booking if the rescheduling\npolicy allows it.\nWhen passing `true`, the rescheduling policy is ignored and external\nvalidation providers are also skipped.\nDefault: `false`.'
            )
            .optional(),
          skipAvailabilityValidation: z
            .boolean()
            .describe(
              'Whether the availability is checked before rescheduling the booking.\n\nWhen passing `false`, a booking is only created when the slot or\nschedule is available.\nDefault: `false`.'
            )
            .optional(),
          skipBusinessConfirmation: z
            .boolean()
            .describe(
              "Whether the rescheduled booking's status is automatically set to\n`CONFIRMED` for services that normally require the owner's manual\nconfirmation.\nDefault: `false`."
            )
            .optional(),
          allowAddOnChanges: z
            .boolean()
            .describe(
              'Whether you can update the list of add-ons associated with the booking when rescheduling.\nThe calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions to specify `true`.\n\nDefault: `false`.'
            )
            .optional(),
        })
        .describe(
          "Information about whether specific procedures of the standard Wix Bookings rescheduling flow are changed.\nFor example, whether the availability of the new slot is checked before rescheduling the booking or if the customer can reschedule the booking even though the service's rescheduling policy doesn't allow it."
        )
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the rescheduled bookings entities.')
        .optional(),
    })
    .optional(),
});
export const RescheduleMultiServiceBookingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe('Rescheduled multi-service booking.')
    .optional(),
});
export const GetMultiServiceBookingAvailabilityRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetMultiServiceBookingAvailabilityResponse = z.object({
  bookable: z
    .boolean()
    .describe('Whether all contained single-service bookings are bookable.')
    .optional(),
  totalCapacity: z
    .number()
    .int()
    .describe('Total number of spots.')
    .optional()
    .nullable(),
  remainingCapacity: z
    .number()
    .int()
    .describe('Number of open spots.')
    .optional()
    .nullable(),
  policyViolations: z
    .object({
      tooEarlyToBook: z
        .boolean()
        .describe('Booking policy violation: Too early to book this slot.')
        .optional()
        .nullable(),
      tooLateToBook: z
        .boolean()
        .describe('Booking policy violation: Too late to book this slot.')
        .optional()
        .nullable(),
      bookOnlineDisabled: z
        .boolean()
        .describe(
          'Booking policy violation: Online booking is disabled for this slot.'
        )
        .optional()
        .nullable(),
    })
    .describe('Indicators for policy violations of the multi-service booking.')
    .optional(),
  policySettings: z
    .object({
      maxParticipantsPerBooking: z
        .number()
        .int()
        .describe('Booking policy settings for a given slot or schedule.')
        .optional()
        .nullable(),
    })
    .describe('Multi-service booking policy settings.')
    .optional(),
  multiServiceBookingInfo: z
    .array(
      z.object({
        bookingId: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const CancelMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking to cancel.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      flowControlSettings: z
        .object({
          ignoreCancellationPolicy: z
            .boolean()
            .describe(
              'Whether the cancellation policy applies when canceling the booking.\n\nWhen passing `false`, you can only cancel a booking if the cancellation policy allows it.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          withRefund: z
            .boolean()
            .describe(
              'Whether to issue a refund when canceling the booking.\n\nThe refund will be issued only if the booking is refundable.\nCurrently, a booking is considered refundable when it was paid by membership.\nIf you specify `true`, the booking flow control settings is set to include a refund.\nIf `false` is specified or the field remains empty,\nthe booking flow control settings are set without refund.\n\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          waiveCancellationFee: z
            .boolean()
            .describe(
              "Whether to waive the cancellation fee when canceling the booking.\n\n- `true`: The customer doesn't have to pay a cancellation fee.\n- `false`: The customer must pay the cancellation fee that's defined in the service's [policy](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).\n\nDefault: `false`."
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about whether specific procedures of the standard Wix Bookings cancellation flow are changed.\nFor example, whether the customer can cancel the booking even though the service's cancellation policy doesn't allow it or whether to issue a refund upon cancellation."
        )
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the canceled single-service bookings.')
        .optional(),
    })
    .optional(),
});
export const CancelMultiServiceBookingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe('Canceled multi-service booking.')
    .optional(),
});
export const MarkMultiServiceBookingAsPendingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking to mark as `PENDING`.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      markAsPendingBookingsInfo: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe('ID of the single-service booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
              )
              .optional()
              .nullable(),
            paymentStatus: z
              .enum([
                'UNDEFINED',
                'NOT_PAID',
                'PAID',
                'PARTIALLY_PAID',
                'REFUNDED',
                'EXEMPT',
              ])
              .optional(),
          })
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the session starts.\nWix Bookings takes the phone number from `contactDetails.phone`.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the single-service bookings that were marked as `PENDING`.\n\nDefault: `false`'
        )
        .optional(),
      flowControlSettings: z
        .object({
          checkAvailabilityValidation: z
            .boolean()
            .describe(
              'Whether to check for double booking conflicts when updating the status.\n\n- `true`: Checks for availability conflicts and sets `doubleBooked` to `true`\nif conflicts exist.\nThe booking is still marked as `PENDING` for the business to resolve manually.\n- `false`: No availability checking is performed.\n\nDefault: `false`'
            )
            .optional(),
          skipPendingApprovalServiceValidation: z
            .boolean()
            .describe(
              "Whether to allow marking any booking as pending, even if the service doesn't require manual approval.\n\n- `true`: Bypasses the validation that checks if the service has `service.onlineBooking.requireManualApproval` set to `true`.\n- `false`: Only services that require manual approval can have their bookings marked as pending.\n\nDefault: `false`."
            )
            .optional(),
        })
        .describe(
          'Information about whether specific procedures of the standard Wix Bookings creation flow are changed.\nFor example, whether Wix Bookings checks availability before updating the booking.'
        )
        .optional(),
    })
    .optional(),
});
export const MarkMultiServiceBookingAsPendingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe('Updated multi-service booking.')
    .optional(),
});
export const ConfirmMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe(
      'ID of the multi-service booking to confirm its related bookings.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      confirmBookingsInfo: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe('ID of the single-service booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
              )
              .optional()
              .nullable(),
            paymentStatus: z
              .enum([
                'UNDEFINED',
                'NOT_PAID',
                'PAID',
                'PARTIALLY_PAID',
                'REFUNDED',
                'EXEMPT',
              ])
              .optional(),
          })
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the session starts.\nWix Bookings takes the phone number from `contactDetails.phone`.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the confirmed single-service bookings.')
        .optional(),
      flowControlSettings: z
        .object({
          checkAvailabilityValidation: z
            .boolean()
            .describe(
              'Whether the availability is checked before confirming the booking.\n\nWhen specifying `false`, a booking is only updated with status `CONFIRMED`.\nDefault: `false`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether specific procedures of the standard Wix Bookings confirmation flow are changed.\nFor example, whether Wix Bookings checks availability before confirming the booking.'
        )
        .optional(),
    })
    .optional(),
});
export const ConfirmMultiServiceBookingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe('Confirmed multi-service booking.')
    .optional(),
});
export const DeclineMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking to decline.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      declineBookingsInfo: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe('ID of the single-service booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
              )
              .optional()
              .nullable(),
            paymentStatus: z
              .enum([
                'UNDEFINED',
                'NOT_PAID',
                'PAID',
                'PARTIALLY_PAID',
                'REFUNDED',
                'EXEMPT',
              ])
              .optional(),
          })
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and the message to send.'
        )
        .optional(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the declined single-service bookings.')
        .optional(),
      flowControlSettings: z
        .object({
          withRefund: z
            .boolean()
            .describe(
              'Whether to issue a refund when declining the booking.\n\nThe refund will be issued only if the booking is refundable.\nCurrently, a booking is considered refundable when it was paid by membership.\nIf specifying `true`, the booking flow control settings will be set with a refund.\nIf you specify `false` or an empty field,\nthe booking flow control settings are set without refund.\n\nDefault: `false`.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Information about whether specific procedures of the standard Wix Bookings declining flow are changed.\nFor example, whether to issue a refund upon cancellation.'
        )
        .optional(),
    })
    .optional(),
});
export const DeclineMultiServiceBookingResponse = z.object({
  multiServiceBooking: z
    .object({
      _id: z
        .string()
        .describe('Multi-service booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe(
                'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            booking: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Booking ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedEntity: z
                    .intersection(
                      z.object({
                        title: z
                          .string()
                          .describe(
                            'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                          )
                          .max(6000)
                          .optional()
                          .nullable(),
                        tags: z.array(z.string()).optional(),
                      }),
                      z.xor([
                        z.object({
                          slot: z.never().optional(),
                          schedule: z.never().optional(),
                        }),
                        z.object({
                          schedule: z.never().optional(),
                          slot: z
                            .object({
                              sessionId: z
                                .string()
                                .describe(
                                  'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                                )
                                .optional()
                                .nullable(),
                              serviceId: z
                                .string()
                                .describe('Service ID.')
                                .optional(),
                              scheduleId: z
                                .string()
                                .describe('Schedule ID.')
                                .optional(),
                              eventId: z
                                .string()
                                .describe(
                                  'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                                )
                                .min(36)
                                .max(250)
                                .optional()
                                .nullable(),
                              startDate: z
                                .string()
                                .describe(
                                  'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              endDate: z
                                .string()
                                .describe(
                                  'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              resource: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      "ID of the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe(
                                      "Resource's name at the time of booking."
                                    )
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                  email: z
                                    .string()
                                    .describe(
                                      "Resource's email at the time of booking."
                                    )
                                    .max(500)
                                    .optional()
                                    .nullable(),
                                  scheduleId: z
                                    .string()
                                    .describe(
                                      "ID of the schedule belonging to the booking's primary resource."
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                                )
                                .optional(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  'Location where the session takes place.'
                                )
                                .optional(),
                              resourceSelections: z
                                .array(
                                  z.object({
                                    resourceTypeId: z
                                      .string()
                                      .describe(
                                        'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    selectionMethod: z
                                      .enum([
                                        'SPECIFIC_RESOURCE',
                                        'ANY_RESOURCE',
                                        'NO_SELECTION',
                                      ])
                                      .describe(
                                        'Information about how the customer has selected the resource for this resource type.'
                                      )
                                      .optional(),
                                  })
                                )
                                .max(3)
                                .optional(),
                            })
                            .describe(
                              '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                            ),
                        }),
                        z.object({
                          slot: z.never().optional(),
                          schedule: z
                            .object({
                              scheduleId: z
                                .string()
                                .describe(
                                  '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                                )
                                .optional(),
                              serviceId: z
                                .string()
                                .describe('Booked service ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              location: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe(
                                      'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional()
                                    .nullable(),
                                  name: z
                                    .string()
                                    .describe('Location name.')
                                    .optional()
                                    .nullable(),
                                  formattedAddress: z
                                    .string()
                                    .describe(
                                      'The full address of this location.'
                                    )
                                    .optional()
                                    .nullable(),
                                  formattedAddressTranslated: z
                                    .string()
                                    .describe(
                                      'The full translated address of this location.'
                                    )
                                    .max(512)
                                    .optional()
                                    .nullable(),
                                  locationType: z
                                    .enum([
                                      'UNDEFINED',
                                      'OWNER_BUSINESS',
                                      'OWNER_CUSTOM',
                                      'CUSTOM',
                                    ])
                                    .describe('Location type.')
                                    .optional(),
                                })
                                .describe(
                                  "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                                )
                                .optional(),
                              timezone: z
                                .string()
                                .describe(
                                  'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                                )
                                .optional()
                                .nullable(),
                              firstSessionStart: z
                                .string()
                                .describe(
                                  'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                              lastSessionEnd: z
                                .string()
                                .describe(
                                  'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      contactId: z
                        .string()
                        .describe('Contact ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      firstName: z
                        .string()
                        .describe(
                          "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                        )
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe(
                          "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                        )
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      fullAddress: z
                        .intersection(
                          z.object({
                            country: z
                              .string()
                              .describe('Country code.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .optional()
                              .nullable(),
                            postalCode: z
                              .string()
                              .describe('Zip/postal code.')
                              .optional()
                              .nullable(),
                            addressLine2: z
                              .string()
                              .describe(
                                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                              )
                              .optional()
                              .nullable(),
                            formattedAddress: z
                              .string()
                              .describe(
                                'A string containing the full address of this location.'
                              )
                              .optional()
                              .nullable(),
                            hint: z
                              .string()
                              .describe('Free text to help find the address.')
                              .optional()
                              .nullable(),
                            geocode: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Coordinates of the physical address.')
                              .optional(),
                            countryFullname: z
                              .string()
                              .describe('Country full name.')
                              .optional()
                              .nullable(),
                            subdivisions: z
                              .array(
                                z.object({
                                  code: z
                                    .string()
                                    .describe(
                                      'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                    )
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Subdivision full name.')
                                    .optional(),
                                })
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z.never().optional(),
                            }),
                            z.object({
                              addressLine: z.never().optional(),
                              streetAddress: z
                                .object({
                                  number: z
                                    .string()
                                    .describe('Street number.')
                                    .optional(),
                                  name: z
                                    .string()
                                    .describe('Street name.')
                                    .optional(),
                                  apt: z
                                    .string()
                                    .describe('Apartment number.')
                                    .optional(),
                                })
                                .describe(
                                  'Street name, number and apartment number.'
                                ),
                            }),
                            z.object({
                              streetAddress: z.never().optional(),
                              addressLine: z
                                .string()
                                .describe(
                                  'Main address line, usually street and number, as free text.'
                                ),
                            }),
                          ])
                        )
                        .describe("Contact's full address.")
                        .optional(),
                      timeZone: z
                        .string()
                        .describe("Contact's time zone.")
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                    )
                    .optional(),
                  additionalFields: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe(
                            'ID of the form field as defined in the form.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe('Value that was submitted for this field.')
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            "Form field's label at the time of submission."
                          )
                          .optional()
                          .nullable(),
                        valueType: z
                          .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                          .optional(),
                      })
                    )
                    .optional(),
                  status: z
                    .enum([
                      'CREATED',
                      'CONFIRMED',
                      'CANCELED',
                      'PENDING',
                      'DECLINED',
                      'WAITING_LIST',
                    ])
                    .describe(
                      "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                    )
                    .optional(),
                  paymentStatus: z
                    .enum([
                      'UNDEFINED',
                      'NOT_PAID',
                      'PAID',
                      'PARTIALLY_PAID',
                      'REFUNDED',
                      'EXEMPT',
                    ])
                    .describe(
                      "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                    )
                    .optional(),
                  selectedPaymentOption: z
                    .enum([
                      'UNDEFINED',
                      'OFFLINE',
                      'ONLINE',
                      'MEMBERSHIP',
                      'MEMBERSHIP_OFFLINE',
                    ])
                    .describe(
                      "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  externalUserId: z
                    .string()
                    .describe('External user ID that you can provide.')
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                    )
                    .optional()
                    .nullable(),
                  createdBy: z
                    .intersection(
                      z.object({
                        contactId: z
                          .string()
                          .describe(
                            "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                        }),
                        z.object({
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          anonymousVisitorId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has not logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z.never().optional(),
                          memberId: z
                            .string()
                            .describe(
                              'ID of a site visitor that has logged in to the site.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          appId: z.never().optional(),
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user (site owner, contributor, etc.).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                        z.object({
                          anonymousVisitorId: z.never().optional(),
                          memberId: z.never().optional(),
                          wixUserId: z.never().optional(),
                          appId: z
                            .string()
                            .describe('ID of an app.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe(
                      'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                    )
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe(
                      'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                    )
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                    )
                    .optional()
                    .nullable(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  doubleBooked: z
                    .boolean()
                    .describe(
                      'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                    )
                    .optional()
                    .nullable(),
                  formSubmissionId: z
                    .string()
                    .describe(
                      'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  formId: z
                    .string()
                    .describe(
                      "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  bookedAddOns: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('The ID of the add-on.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        groupId: z
                          .string()
                          .describe('The ID of the add-on group.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        durationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                          )
                          .min(1)
                          .max(1440)
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe(
                            'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                          )
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Add-on `name` at the time of booking.')
                          .max(100)
                          .optional()
                          .nullable(),
                        nameTranslated: z
                          .string()
                          .describe(
                            'Add-on name translated to the language the customer used during booking.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(21)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  createdByAppId: z
                    .string()
                    .describe(
                      "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  depositSelected: z
                    .boolean()
                    .describe(
                      'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z.never().optional(),
                  }),
                  z.object({
                    participantsChoices: z.never().optional(),
                    totalParticipants: z
                      .number()
                      .int()
                      .describe(
                        "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                      )
                      .min(1),
                  }),
                  z.object({
                    totalParticipants: z.never().optional(),
                    participantsChoices: z
                      .object({
                        serviceChoices: z
                          .array(
                            z.object({
                              numberOfParticipants: z
                                .number()
                                .int()
                                .describe(
                                  'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      optionId: z
                                        .string()
                                        .describe(
                                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.never().optional(),
                                      }),
                                      z.object({
                                        duration: z.never().optional(),
                                        custom: z
                                          .string()
                                          .describe(
                                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                          ),
                                      }),
                                      z.object({
                                        custom: z.never().optional(),
                                        duration: z.object({
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                            )
                                            .min(1)
                                            .max(44639)
                                            .optional(),
                                          name: z
                                            .string()
                                            .describe(
                                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                            )
                                            .max(255)
                                            .optional()
                                            .nullable(),
                                        }),
                                      }),
                                    ])
                                  )
                                )
                                .max(5)
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(20)
                          .optional(),
                      })
                      .describe(
                        'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                      ),
                  }),
                ])
              )
              .describe(
                'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
              )
              .optional(),
          })
        )
        .optional(),
    })
    .describe('Declined multi-service booking.')
    .optional(),
});
export const BulkGetMultiServiceBookingAllowedActionsRequest = z.object({
  multiServiceBookingIds: z.array(z.string()).min(1).max(50),
});
export const BulkGetMultiServiceBookingAllowedActionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Metadata for the booking. Including ID, index in the provided sequence, success status, and error.'
          )
          .optional(),
        item: z
          .object({
            cancel: z
              .boolean()
              .describe('Whether canceling the booking is allowed.')
              .optional(),
            reschedule: z
              .boolean()
              .describe('Whether rescheduling the booking is allowed.')
              .optional(),
          })
          .describe('Booking entity.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Get Multi Service Booking Allowed Actions.'
    )
    .optional(),
});
export const GetMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetMultiServiceBookingResponse = z.object({
  _id: z
    .string()
    .describe('Multi-service booking ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  bookings: z
    .array(
      z.object({
        bookingId: z
          .string()
          .describe(
            'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        booking: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      slot: z.never().optional(),
                      schedule: z.never().optional(),
                    }),
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .describe(
                                    'Information about how the customer has selected the resource for this resource type.'
                                  )
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                )
                .optional(),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .describe(
                  "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                )
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .describe(
                  "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                )
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .describe(
                  "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                )
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe(
            'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const AddBookingsToMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    bookings: z
      .array(
        z.object({
          bookingId: z
            .string()
            .describe('ID of the single-service booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
            ),
        })
      )
      .max(8),
    returnFullEntity: z
      .boolean()
      .describe(
        'Whether to return the single-service bookings that were added to the multi-service booking.\n\nDefault: `false`'
      )
      .optional(),
  }),
});
export const AddBookingsToMultiServiceBookingResponse = z.object({
  bookings: z
    .array(
      z.object({
        bookingId: z
          .string()
          .describe(
            'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        booking: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      slot: z.never().optional(),
                      schedule: z.never().optional(),
                    }),
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .describe(
                                    'Information about how the customer has selected the resource for this resource type.'
                                  )
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                )
                .optional(),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .describe(
                  "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                )
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .describe(
                  "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                )
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .describe(
                  "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                )
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe(
            'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const RemoveBookingsFromMultiServiceBookingRequest = z.object({
  multiServiceBookingId: z
    .string()
    .describe('ID of the multi-service booking.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      bookings: z
        .array(
          z.object({
            bookingId: z
              .string()
              .describe('ID of the single-service booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be specified when managing the booking.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(8)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the single-service bookings.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const RemoveBookingsFromMultiServiceBookingResponse = z.object({
  bookings: z
    .array(
      z.object({
        bookingId: z
          .string()
          .describe(
            'Single-service booking ID. Always present.\nThis value is identical to `booking.id`, which is only returned when `returnFullEntity` is `true`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        booking: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      slot: z.never().optional(),
                      schedule: z.never().optional(),
                    }),
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .describe(
                                    'Information about how the customer has selected the resource for this resource type.'
                                  )
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                )
                .optional(),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .describe(
                  "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                )
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .describe(
                  "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                )
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .describe(
                  "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                )
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe(
            'Single-service booking entity. Only returned when `returnFullEntity` is `true`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const CreateBookingRequest = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          ),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('The booking to create.'),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and\nthe message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the\nsession starts. The phone number is taken from `contactDetails.phone`.\nDefault: `true`.'
        )
        .optional()
        .nullable(),
      flowControlSettings: z
        .object({
          skipAvailabilityValidation: z
            .boolean()
            .describe(
              "Whether the availability is checked before creating the booking.\n\n- `false`: A booking is only created when the slot or schedule is available.\n- `true`: The booking is created regardless of availability conflicts. Make sure the call's [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) has the required permissions.\n\n*Use cases for `true`:**\n- Emergency or priority bookings that must be accommodated.\n- Administrative bookings that override normal availability rules.\n- Testing or demonstration purposes.\n\nDefault: `false`."
            )
            .optional(),
          skipBusinessConfirmation: z
            .boolean()
            .describe(
              "Whether `PENDING` bookings are automatically set to `CONFIRMED` for\nservices that normally require the owner's manual confirmation.\n\nYour app must have the `BOOKINGS.OVERRIDE_AVAILABILITY` permission\nwhen passing `true`.\nDefault: `false`."
            )
            .optional(),
          skipSelectedPaymentOptionValidation: z
            .boolean()
            .describe(
              "Whether customers can pay using a payment method that isn't supported\nfor the service, but that's supported for other services.\n\nYour app must have the `BOOKINGS.MANAGE_PAYMENTS` permission when passing\n`true`.\nDefault: `false`."
            )
            .optional(),
          skipAddOnValidation: z
            .boolean()
            .describe(
              "Whether Wix Bookings validates [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) when creating a booking.\nThe calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions to specify `true`.\nThis allows customers to choose an add-on that's not associated with the service or choose more than the maximum number of add-ons from a group.\n\nDefault: `false`."
            )
            .optional(),
        })
        .describe(
          'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to check availability when creating a booking.'
        )
        .optional(),
      formSubmission: z
        .record(z.string(), z.any())
        .describe(
          "Booking form data submitted by the customer during the booking process.\nEach key represents the name of the form submission field while the value is the data submitted by the customer for that field.\n\nWix Bookings stores this form data in different locations depending on the field type:\n- __Contact details fields__: Input values for standard contact fields such as name, email, phone, and address are saved in the booking object's `contactDetails`.\n- __Custom and additional fields__: All other form field values are stored separately in the [form submissions object](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction).\n\nFor comprehensive details about integrating custom forms with bookings, see [Wix Forms Integration](https://dev.wix.com/docs/rest/business-solutions/bookings/wix-forms-integration)."
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CreateBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Created booking.')
    .optional(),
});
export const BulkCreateBookingRequest = z.object({
  createBookingsInfo: z
    .array(
      z.object({
        booking: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                ),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe('Booking to create.'),
        participantNotification: z
          .object({
            notifyParticipants: z
              .boolean()
              .describe(
                'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
              )
              .optional(),
            message: z
              .string()
              .describe(
                'Custom message to send to the participants about the changes to the booking.'
              )
              .optional()
              .nullable(),
            metadata: z
              .record(z.string(), z.string())
              .describe(
                'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
              )
              .optional(),
          })
          .describe(
            'Information about whether to notify the customer and\nthe message to send.'
          )
          .optional(),
        sendSmsReminder: z
          .boolean()
          .describe(
            'Whether to send an SMS reminder to the customer 24 hours before the\nsession starts. The phone number is taken from `contactDetails.phone`.\nDefault: `true`.'
          )
          .optional()
          .nullable(),
        flowControlSettings: z
          .object({
            skipAvailabilityValidation: z
              .boolean()
              .describe(
                "Whether the availability is checked before creating the booking.\n\n- `false`: A booking is only created when the slot or schedule is available.\n- `true`: The booking is created regardless of availability conflicts. Make sure the call's [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) has the required permissions.\n\n*Use cases for `true`:**\n- Emergency or priority bookings that must be accommodated.\n- Administrative bookings that override normal availability rules.\n- Testing or demonstration purposes.\n\nDefault: `false`."
              )
              .optional(),
            skipBusinessConfirmation: z
              .boolean()
              .describe(
                "Whether `PENDING` bookings are automatically set to `CONFIRMED` for\nservices that normally require the owner's manual confirmation.\n\nYour app must have the `BOOKINGS.OVERRIDE_AVAILABILITY` permission\nwhen passing `true`.\nDefault: `false`."
              )
              .optional(),
            skipSelectedPaymentOptionValidation: z
              .boolean()
              .describe(
                "Whether customers can pay using a payment method that isn't supported\nfor the service, but that's supported for other services.\n\nYour app must have the `BOOKINGS.MANAGE_PAYMENTS` permission when passing\n`true`.\nDefault: `false`."
              )
              .optional(),
            skipAddOnValidation: z
              .boolean()
              .describe(
                "Whether Wix Bookings validates [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) when creating a booking.\nThe calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions to specify `true`.\nThis allows customers to choose an add-on that's not associated with the service or choose more than the maximum number of add-ons from a group.\n\nDefault: `false`."
              )
              .optional(),
          })
          .describe(
            'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to check availability when creating a booking.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(12),
  options: z
    .object({
      returnFullEntity: z
        .boolean()
        .describe('Whether to return the created bookings.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateBookingResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the booking that was created or updated.\nIncluding its ID, index in the bulk request and whether it was\nsuccessfully created or updated.'
          )
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Booking ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedEntity: z
                .intersection(
                  z.object({
                    title: z
                      .string()
                      .describe(
                        'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                      )
                      .max(6000)
                      .optional()
                      .nullable(),
                    tags: z.array(z.string()).optional(),
                  }),
                  z.xor([
                    z.object({
                      slot: z.never().optional(),
                      schedule: z.never().optional(),
                    }),
                    z.object({
                      schedule: z.never().optional(),
                      slot: z
                        .object({
                          sessionId: z
                            .string()
                            .describe(
                              'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                            )
                            .optional()
                            .nullable(),
                          serviceId: z
                            .string()
                            .describe('Service ID.')
                            .optional(),
                          scheduleId: z
                            .string()
                            .describe('Schedule ID.')
                            .optional(),
                          eventId: z
                            .string()
                            .describe(
                              'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                            )
                            .min(36)
                            .max(250)
                            .optional()
                            .nullable(),
                          startDate: z
                            .string()
                            .describe(
                              'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          endDate: z
                            .string()
                            .describe(
                              'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          resource: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  "ID of the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  "Resource's name at the time of booking."
                                )
                                .max(40)
                                .optional()
                                .nullable(),
                              email: z
                                .string()
                                .describe(
                                  "Resource's email at the time of booking."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              scheduleId: z
                                .string()
                                .describe(
                                  "ID of the schedule belonging to the booking's primary resource."
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                            )
                            .optional(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe('Location where the session takes place.')
                            .optional(),
                          resourceSelections: z
                            .array(
                              z.object({
                                resourceTypeId: z
                                  .string()
                                  .describe(
                                    'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                selectionMethod: z
                                  .enum([
                                    'SPECIFIC_RESOURCE',
                                    'ANY_RESOURCE',
                                    'NO_SELECTION',
                                  ])
                                  .describe(
                                    'Information about how the customer has selected the resource for this resource type.'
                                  )
                                  .optional(),
                              })
                            )
                            .max(3)
                            .optional(),
                        })
                        .describe(
                          '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                        ),
                    }),
                    z.object({
                      slot: z.never().optional(),
                      schedule: z
                        .object({
                          scheduleId: z
                            .string()
                            .describe(
                              '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                            )
                            .optional(),
                          serviceId: z
                            .string()
                            .describe('Booked service ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe('Location name.')
                                .optional()
                                .nullable(),
                              formattedAddress: z
                                .string()
                                .describe('The full address of this location.')
                                .optional()
                                .nullable(),
                              formattedAddressTranslated: z
                                .string()
                                .describe(
                                  'The full translated address of this location.'
                                )
                                .max(512)
                                .optional()
                                .nullable(),
                              locationType: z
                                .enum([
                                  'UNDEFINED',
                                  'OWNER_BUSINESS',
                                  'OWNER_CUSTOM',
                                  'CUSTOM',
                                ])
                                .describe('Location type.')
                                .optional(),
                            })
                            .describe(
                              "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                            )
                            .optional(),
                          timezone: z
                            .string()
                            .describe(
                              'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                            )
                            .optional()
                            .nullable(),
                          firstSessionStart: z
                            .string()
                            .describe(
                              'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                          lastSessionEnd: z
                            .string()
                            .describe(
                              'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
                )
                .optional(),
              contactDetails: z
                .object({
                  contactId: z
                    .string()
                    .describe('Contact ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstName: z
                    .string()
                    .describe(
                      "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
                    )
                    .optional()
                    .nullable(),
                  lastName: z
                    .string()
                    .describe("Contact's last name.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe(
                      "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
                    )
                    .email()
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Contact's phone number.")
                    .optional()
                    .nullable(),
                  fullAddress: z
                    .intersection(
                      z.object({
                        country: z
                          .string()
                          .describe('Country code.')
                          .optional()
                          .nullable(),
                        subdivision: z
                          .string()
                          .describe(
                            'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional()
                          .nullable(),
                        city: z
                          .string()
                          .describe('City name.')
                          .optional()
                          .nullable(),
                        postalCode: z
                          .string()
                          .describe('Zip/postal code.')
                          .optional()
                          .nullable(),
                        addressLine2: z
                          .string()
                          .describe(
                            'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                          )
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe(
                            'A string containing the full address of this location.'
                          )
                          .optional()
                          .nullable(),
                        hint: z
                          .string()
                          .describe('Free text to help find the address.')
                          .optional()
                          .nullable(),
                        geocode: z
                          .object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Coordinates of the physical address.')
                          .optional(),
                        countryFullname: z
                          .string()
                          .describe('Country full name.')
                          .optional()
                          .nullable(),
                        subdivisions: z
                          .array(
                            z.object({
                              code: z
                                .string()
                                .describe(
                                  'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Subdivision full name.')
                                .optional(),
                            })
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z.never().optional(),
                        }),
                        z.object({
                          addressLine: z.never().optional(),
                          streetAddress: z
                            .object({
                              number: z
                                .string()
                                .describe('Street number.')
                                .optional(),
                              name: z
                                .string()
                                .describe('Street name.')
                                .optional(),
                              apt: z
                                .string()
                                .describe('Apartment number.')
                                .optional(),
                            })
                            .describe(
                              'Street name, number and apartment number.'
                            ),
                        }),
                        z.object({
                          streetAddress: z.never().optional(),
                          addressLine: z
                            .string()
                            .describe(
                              'Main address line, usually street and number, as free text.'
                            ),
                        }),
                      ])
                    )
                    .describe("Contact's full address.")
                    .optional(),
                  timeZone: z
                    .string()
                    .describe("Contact's time zone.")
                    .optional()
                    .nullable(),
                  countryCode: z
                    .string()
                    .describe(
                      "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
                )
                .optional(),
              additionalFields: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('ID of the form field as defined in the form.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe('Value that was submitted for this field.')
                      .optional()
                      .nullable(),
                    label: z
                      .string()
                      .describe("Form field's label at the time of submission.")
                      .optional()
                      .nullable(),
                    valueType: z
                      .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                      .optional(),
                  })
                )
                .optional(),
              status: z
                .enum([
                  'CREATED',
                  'CONFIRMED',
                  'CANCELED',
                  'PENDING',
                  'DECLINED',
                  'WAITING_LIST',
                ])
                .describe(
                  "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
                )
                .optional(),
              paymentStatus: z
                .enum([
                  'UNDEFINED',
                  'NOT_PAID',
                  'PAID',
                  'PARTIALLY_PAID',
                  'REFUNDED',
                  'EXEMPT',
                ])
                .describe(
                  "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
                )
                .optional(),
              selectedPaymentOption: z
                .enum([
                  'UNDEFINED',
                  'OFFLINE',
                  'ONLINE',
                  'MEMBERSHIP',
                  'MEMBERSHIP_OFFLINE',
                ])
                .describe(
                  "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
                )
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              externalUserId: z
                .string()
                .describe('External user ID that you can provide.')
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
                )
                .optional()
                .nullable(),
              createdBy: z
                .intersection(
                  z.object({
                    contactId: z
                      .string()
                      .describe(
                        "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                    }),
                    z.object({
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      anonymousVisitorId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has not logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z.never().optional(),
                      memberId: z
                        .string()
                        .describe(
                          'ID of a site visitor that has logged in to the site.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      appId: z.never().optional(),
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user (site owner, contributor, etc.).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      anonymousVisitorId: z.never().optional(),
                      memberId: z.never().optional(),
                      wixUserId: z.never().optional(),
                      appId: z
                        .string()
                        .describe('ID of an app.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
                )
                .optional(),
              startDate: z
                .date()
                .describe(
                  'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              doubleBooked: z
                .boolean()
                .describe(
                  'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
                )
                .optional()
                .nullable(),
              formSubmissionId: z
                .string()
                .describe(
                  'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              formId: z
                .string()
                .describe(
                  "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              bookedAddOns: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('The ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    groupId: z
                      .string()
                      .describe('The ID of the add-on group.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                      )
                      .min(1)
                      .max(1440)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                      )
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Add-on `name` at the time of booking.')
                      .max(100)
                      .optional()
                      .nullable(),
                    nameTranslated: z
                      .string()
                      .describe(
                        'Add-on name translated to the language the customer used during booking.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                )
                .max(21)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              depositSelected: z
                .boolean()
                .describe(
                  'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z.never().optional(),
              }),
              z.object({
                participantsChoices: z.never().optional(),
                totalParticipants: z
                  .number()
                  .int()
                  .describe(
                    "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
                  )
                  .min(1),
              }),
              z.object({
                totalParticipants: z.never().optional(),
                participantsChoices: z
                  .object({
                    serviceChoices: z
                      .array(
                        z.object({
                          numberOfParticipants: z
                            .number()
                            .int()
                            .describe(
                              'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.intersection(
                                z.object({
                                  optionId: z
                                    .string()
                                    .describe(
                                      'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.never().optional(),
                                  }),
                                  z.object({
                                    duration: z.never().optional(),
                                    custom: z
                                      .string()
                                      .describe(
                                        'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                      ),
                                  }),
                                  z.object({
                                    custom: z.never().optional(),
                                    duration: z.object({
                                      minutes: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                        )
                                        .min(1)
                                        .max(44639)
                                        .optional(),
                                      name: z
                                        .string()
                                        .describe(
                                          'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                        )
                                        .max(255)
                                        .optional()
                                        .nullable(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .max(5)
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(20)
                      .optional(),
                  })
                  .describe(
                    'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
                  ),
              }),
            ])
          )
          .describe(
            'Created or updated booking. Available only if you requested\nto return the booking entity.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Create Bookings.'
    )
    .optional(),
});
export const RescheduleBookingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to reschedule.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  slot: z
    .object({
      sessionId: z
        .string()
        .describe(
          'Identifier for the underlying session when the session is a single session or generated from a recurring session.'
        )
        .optional()
        .nullable(),
      serviceId: z
        .string()
        .describe('Service identifier. Required.')
        .optional(),
      scheduleId: z
        .string()
        .describe('Schedule identifier. Required.')
        .optional(),
      startDate: z
        .string()
        .describe(
          'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
        )
        .optional()
        .nullable(),
      endDate: z
        .string()
        .describe(
          'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
        )
        .optional()
        .nullable(),
      timezone: z
        .string()
        .describe(
          'The timezone according to which the slot is calculated and presented.'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe('Resource ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Resource name.')
            .max(1200)
            .optional()
            .nullable(),
          transparency: z.enum(['OPAQUE', 'TRANSPARENT']).optional(),
        })
        .describe(
          'The resource required for this slot.\nWhen populated, the specified resource will be assigned to the slot upon confirmation according to its availability.\nWhen empty, if `skip_availability_validation` is `false`, a random available resource will be assigned to the slot upon confirmation.\nOtherwise, one of the service resources will be assigned to the slot randomly upon confirmation.'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              'Business Location ID. Present if the location is a business location.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z.string().describe('Location name.').optional().nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          locationType: z
            .enum(['UNDEFINED', 'OWNER_BUSINESS', 'OWNER_CUSTOM', 'CUSTOM'])
            .optional(),
        })
        .describe('Geographic location of the slot.')
        .optional(),
      eventId: z
        .string()
        .describe(
          'Calendar event ID - not supported.\nIf not empty, on all write flows (create/update), it takes priority over `sessionId`.\nSo if both `sessionId` and `eventId` are provided, the `sessionId` will be based on the `eventId`.\nOtherwise, if `eventId` is empty on write flow,'
        )
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      resourceSelections: z
        .array(
          z.object({
            resourceTypeId: z
              .string()
              .describe(
                'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            selectionMethod: z
              .enum(['SPECIFIC_RESOURCE', 'ANY_RESOURCE', 'NO_SELECTION'])
              .optional(),
          })
        )
        .max(3)
        .optional(),
    })
    .describe('New slot of the booking.'),
  options: z
    .intersection(
      z.object({
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the booking is rescheduled.\nTo prevent conflicting changes, the current revision must be passed when\nrescheduling the booking.'
          ),
        participantNotification: z
          .object({
            notifyParticipants: z
              .boolean()
              .describe(
                'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
              )
              .optional(),
            message: z
              .string()
              .describe(
                'Custom message to send to the participants about the changes to the booking.'
              )
              .optional()
              .nullable(),
            metadata: z
              .record(z.string(), z.string())
              .describe(
                'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
              )
              .optional(),
          })
          .describe(
            'Information about whether to notify the customer about the rescheduling and\nthe message to send.'
          )
          .optional(),
        flowControlSettings: z
          .object({
            ignoreReschedulePolicy: z
              .boolean()
              .describe(
                'Whether the rescheduling policy applies when rescheduling the booking.\n\nWhen passing `false`, you can only reschedule a booking if the rescheduling\npolicy allows it.\nWhen passing `true`, the rescheduling policy is ignored and external\nvalidation providers are also skipped.\nDefault: `false`.'
              )
              .optional(),
            skipAvailabilityValidation: z
              .boolean()
              .describe(
                'Whether the availability is checked before rescheduling the booking.\n\nWhen passing `false`, a booking is only created when the slot or\nschedule is available.\nDefault: `false`.'
              )
              .optional(),
            skipBusinessConfirmation: z
              .boolean()
              .describe(
                "Whether the rescheduled booking's status is automatically set to\n`CONFIRMED` for services that normally require the owner's manual\nconfirmation.\nDefault: `false`."
              )
              .optional(),
            allowAddOnChanges: z
              .boolean()
              .describe(
                'Whether you can update the list of add-ons associated with the booking when rescheduling.\nThe calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions to specify `true`.\n\nDefault: `false`.'
              )
              .optional(),
          })
          .describe(
            'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to check availability when rescheduling a booking.'
          )
          .optional(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              'Total number of participants. Specify when all participants have booked the\nsame service variant.'
            ),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the service choices to book. Specify when not all\nparticipants have booked the same service variant.'
            ),
        }),
      ])
    )
    .describe(
      'An object representing the available options for rescheduling a booking. '
    ),
});
export const RescheduleBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Rescheduled booking.')
    .optional(),
});
export const ConfirmBookingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to confirm.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  revision: z
    .object({
      value: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional(),
    })
    .describe(
      'Revision number, which increments by 1 each time the booking is updated.\nTo prevent conflicting changes, the current revision must be passed when\nupdating the booking.'
    ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer about the confirmation and\nthe message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the\nsession starts. The phone number is taken from `contactDetails.phone`.'
        )
        .optional()
        .nullable(),
      paymentStatus: z
        .enum([
          'UNDEFINED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_PAID',
          'REFUNDED',
          'EXEMPT',
        ])
        .optional(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      flowControlSettings: z
        .object({
          checkAvailabilityValidation: z
            .boolean()
            .describe(
              'Whether the availability is checked before confirming the booking.\n\nWhen specifying `false`, a booking is only updated with status `CONFIRMED`.\nDefault: `false`.'
            )
            .optional(),
        })
        .describe(
          'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to check availability when confirming a booking.'
        )
        .optional(),
    })
    .describe(
      'An object representing the available options for canceling a booking.'
    )
    .optional(),
});
export const ConfirmBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Confirmed booking.')
    .optional(),
});
export const SetBookingSubmissionIdRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to set `formSubmissionId` for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  submissionId: z
    .string()
    .describe(
      'ID of the [Wix Forms submission](https://dev.wix.com/docs/api-reference/crm/forms/form-submissions/introduction) to associate with the booking.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SetBookingSubmissionIdResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Updated booking.')
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z
    .string()
    .describe('ID of the booking for which to update extended fields.'),
  namespace: z
    .string()
    .describe(
      '[Namespace](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-reading-and-writing-schema-plugin-fields#namespaces) of the app for which to update extended fields.'
    ),
  options: z
    .object({
      namespaceData: z
        .record(z.string(), z.any())
        .describe(
          'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
        ),
    })
    .describe("Options for updating the booking's extended fields."),
});
export const UpdateExtendedFieldsResponse = z.object({
  namespace: z.string().describe('Updated namespace.').max(164).optional(),
  namespaceData: z
    .record(z.string(), z.any())
    .describe('Updated data.')
    .optional()
    .nullable(),
});
export const DeclineBookingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to decline.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  revision: z
    .object({
      value: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional(),
    })
    .describe(
      'Revision number, which increments by 1 each time the booking is updated.\n\nTo prevent conflicting changes, the current revision must be specified when\ndeclining the booking.'
    ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and\nthe message to send.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'UNDEFINED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_PAID',
          'REFUNDED',
          'EXEMPT',
        ])
        .optional(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      flowControlSettings: z
        .object({
          withRefund: z
            .boolean()
            .describe(
              'Whether to issue a refund when declining the booking.\n\nThe refund will be issued only if the booking is refundable.\nCurrently, a booking is considered refundable when it was paid by membership.\nIf specifying `true`, the booking flow control settings will be set with a refund.\nIf you specify `false` or an empty field,\nthe booking flow control settings are set without refund.\n\nDefault: `false`.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to refund a declined booking.'
        )
        .optional(),
    })
    .describe(
      'An object representing the available options for declining a booking.'
    )
    .optional(),
});
export const DeclineBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Declined booking.')
    .optional(),
});
export const CancelBookingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to cancel.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer about the cancellation and\nthe message to send.'
        )
        .optional(),
      flowControlSettings: z
        .object({
          ignoreCancellationPolicy: z
            .boolean()
            .describe(
              'Whether the cancellation policy applies when canceling the booking.\n\nWhen passing `false`, you can only cancel a booking if the cancellation policy allows it.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          withRefund: z
            .boolean()
            .describe(
              'Whether to issue a refund when canceling the booking.\n\nThe refund will be issued only if the booking is refundable.\nCurrently, a booking is considered refundable when it was paid by membership.\nIf you specify `true`, the booking flow control settings is set to include a refund.\nIf `false` is specified or the field remains empty,\nthe booking flow control settings are set without refund.\n\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          waiveCancellationFee: z
            .boolean()
            .describe(
              "Whether to waive the cancellation fee when canceling the booking.\n\n- `true`: The customer doesn't have to pay a cancellation fee.\n- `false`: The customer must pay the cancellation fee that's defined in the service's [policy](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).\n\nDefault: `false`."
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to allow a cancellation even though the service's\npolicy doesn't allow it."
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the booking is updated.\n\nTo prevent conflicting changes, the current revision must be specified when\nmanaging the booking.'
        ),
    })
    .describe(
      'An object representing the available options for canceling a booking.'
    ),
});
export const CancelBookingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Canceled booking.')
    .optional(),
});
export const UpdateNumberOfParticipantsRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to update the number of participants for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.intersection(
    z.object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the booking is updated.\n\nTo prevent conflicting changes, the current revision must be specified\nwhen updating the booking.'
        ),
    }),
    z.xor([
      z.object({
        totalParticipants: z.never().optional(),
        participantsChoices: z.never().optional(),
      }),
      z.object({
        participantsChoices: z.never().optional(),
        totalParticipants: z
          .number()
          .int()
          .describe(
            'Total number of participants. Specify when all participants have booked the\nsame service variant.'
          ),
      }),
      z.object({
        totalParticipants: z.never().optional(),
        participantsChoices: z
          .object({
            serviceChoices: z
              .array(
                z.object({
                  numberOfParticipants: z
                    .number()
                    .int()
                    .describe(
                      'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          optionId: z
                            .string()
                            .describe(
                              'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            custom: z.never().optional(),
                            duration: z.never().optional(),
                          }),
                          z.object({
                            duration: z.never().optional(),
                            custom: z
                              .string()
                              .describe(
                                'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                              ),
                          }),
                          z.object({
                            custom: z.never().optional(),
                            duration: z.object({
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                )
                                .min(1)
                                .max(44639)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                )
                                .max(255)
                                .optional()
                                .nullable(),
                            }),
                          }),
                        ])
                      )
                    )
                    .max(5)
                    .optional(),
                })
              )
              .min(1)
              .max(20)
              .optional(),
          })
          .describe(
            'Information about the service choices the participants have booked. Specify\nwhen not all participants have booked the same service variant.'
          ),
      }),
    ])
  ),
});
export const UpdateNumberOfParticipantsResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Updated booking.')
    .optional(),
});
export const BulkCalculateAllowedActionsRequest = z.object({
  bookingIds: z.array(z.string()).min(1),
});
export const BulkCalculateAllowedActionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Metadata for the booking. Including ID, index in the provided sequence, success status, and error.'
          )
          .optional(),
        item: z
          .object({
            cancel: z
              .boolean()
              .describe('Whether canceling the booking is allowed.')
              .optional(),
            reschedule: z
              .boolean()
              .describe('Whether rescheduling the booking is allowed.')
              .optional(),
          })
          .describe('Booking entity.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Summary of successful and failed operations.')
    .optional(),
});
export const MarkBookingAsPendingRequest = z.object({
  bookingId: z
    .string()
    .describe('ID of the booking to mark as `PENDING`.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  revision: z
    .object({
      value: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional(),
    })
    .describe(
      'Revision number, which increments by 1 each time the booking is updated.\n\nTo prevent conflicting changes, the current revision must be specified when\nmanaging the booking.'
    ),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.'
            )
            .optional(),
        })
        .describe(
          'Information about whether to notify the customer and\nthe message to send.'
        )
        .optional(),
      sendSmsReminder: z
        .boolean()
        .describe(
          'Whether to send an SMS reminder to the customer 24 hours before the\nsession starts. The phone number is taken from `contactDetails.phone`.'
        )
        .optional()
        .nullable(),
      paymentStatus: z
        .enum([
          'UNDEFINED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_PAID',
          'REFUNDED',
          'EXEMPT',
        ])
        .optional(),
      doubleBooked: z
        .boolean()
        .describe(
          'Whether this booking has a conflict with at least 1 other confirmed booking.'
        )
        .optional()
        .nullable(),
      flowControlSettings: z
        .object({
          checkAvailabilityValidation: z
            .boolean()
            .describe(
              'Whether to check for double booking conflicts when updating the status.\n\n- `true`: Checks for availability conflicts and sets `doubleBooked` to `true`\nif conflicts exist.\nThe booking is still marked as `PENDING` for the business to resolve manually.\n- `false`: No availability checking is performed.\n\nDefault: `false`'
            )
            .optional(),
          skipPendingApprovalServiceValidation: z
            .boolean()
            .describe(
              "Whether to allow marking any booking as pending, even if the service doesn't require manual approval.\n\n- `true`: Bypasses the validation that checks if the service has `service.onlineBooking.requireManualApproval` set to `true`.\n- `false`: Only services that require manual approval can have their bookings marked as pending.\n\nDefault: `false`."
            )
            .optional(),
        })
        .describe(
          'Whether to ignore specific standard procedures of the Wix Bookings flow.\nFor example, whether to check availability before updating the status.'
        )
        .optional(),
    })
    .optional(),
});
export const MarkBookingAsPendingResponse = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .describe(
                              'Information about how the customer has selected the resource for this resource type.'
                            )
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .describe(
            "Booking status. A booking is automatically confirmed if the service allows it\nand an eCommerce order is created. It is automatically declined if there is a\ndouble booking and the customer hasn't paid or is eligible for an automatic\nrefund. Currently, only payments with pricing plans are automatically refundable."
          )
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .describe(
            "The payment status of the booking. This field automatically syncs with the\n`paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)\nwhen customers use Wix eCommerce checkout.\n\n### Integration patterns\n\n__When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.\nDo not manually update this field.\n\n__When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.\n\n__For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.\n\nAll payment statuses are supported for every booking `status`."
          )
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .describe(
            "Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.\n\nWhen the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.\nOtherwise, the Create Booking call fails.\nFor custom checkouts, you don't have to specify this field."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe('Updated booking.')
    .optional(),
});
